import styled from '@emotion/styled'
import { Dimension } from '@pubstack/common/src/analytics/dimension'
import { AnalyticsQueryDashboard, ViewabilityMetricsByTime } from '@pubstack/common/src/analytics/query'
import { useAtom } from 'jotai'
import { DateTime } from 'luxon'
import { FunctionComponent, useState } from 'react'
import { useAnalyticsQueryTanstack } from '~/api/analytics/analytics-query.api'
import { useAuthenticatedUser } from '~/auth/user.hooks'
import { LazyVisible } from '~/components/LazyVisible'
import { useBreadcrumbs } from '~/hooks/useBreadcrumbs'
import { useBreakdownTabs } from '~/hooks/useBreakdownTabs'
import { useLocalStorage } from '~/hooks/useLocalStorage'
import { useScopeCurrency } from '~/hooks/useScopeCurrency.hooks'
import { AnalyticsPage } from '~/modules/analytics/AnalyticsPage'
import { contextState } from '~/state'
import { onBreakdownRowClick } from '~/utils/analytics'
import { PureViewabilityTimeline } from './PureViewabilityTimeline'
import { ViewabilityBreakdown } from './ViewabilityBreakdown'

const PageContent = styled.div`
  display: flex;
  flex-direction: column;
  & > * + * {
    margin-top: 16px;
  }
`

const DIMENSIONS: Dimension[] = ['tagId', 'bidder', 'adUnit', 'device', 'country', 'size', 'pubstackRefresh', 'abTestPopulation', 'stackIdVersion']
const DASHBOARD: AnalyticsQueryDashboard = { name: 'viewability', filterType: 'auctions' }

export const ViewabilityPage: FunctionComponent = () => {
  const [context, setContext] = useAtom(contextState)
  const [breakdownDimension, setBreakdownDimension] = useState<Dimension>('tagId')
  const user = useAuthenticatedUser()
  const currencySymbol = useScopeCurrency()
  const tabsConfig = useBreakdownTabs(user, ['tagId', 'bidder', 'adUnit', 'device', 'country', 'size'], { abTestPopulation: 'abtest', stackIdVersion: 'adstack' })

  const analyticsQueryByTime = useAnalyticsQueryTanstack<ViewabilityMetricsByTime>('viewability.metrics.by.time', DIMENSIONS, context, {})

  const [showMinorData] = useLocalStorage({ key: 'showMinorData', defaultValue: false })

  const onBreakdownTabChange = (dimension: Dimension) => {
    setBreakdownDimension(dimension)
  }

  const onMetricChange = (_metric: string) => {}

  const breadcrumbs = useBreadcrumbs()
  return (
    <AnalyticsPage
      onRefreshClick={() => {
        analyticsQueryByTime.refetch()
      }}
      filterDimensions={DIMENSIONS}
      dashboard={DASHBOARD}
      title={breadcrumbs}
      showMinorDataToggle={true}
    >
      <PageContent>
        <PureViewabilityTimeline
          isLoading={analyticsQueryByTime.isFetching}
          error={!!analyticsQueryByTime.error}
          empty={!analyticsQueryByTime.isLoading && !analyticsQueryByTime.data?.epoch.length}
          onRefreshClick={() => {
            analyticsQueryByTime.refetch()
          }}
          data={analyticsQueryByTime.data ?? { epoch: [] }}
          currencySymbol={currencySymbol}
          currentEpoch={DateTime.now()}
          onMetricChange={onMetricChange}
          timezone={context.timeRange.tz}
        />
        <LazyVisible minHeight={400}>
          <ViewabilityBreakdown
            context={context}
            dimensions={DIMENSIONS}
            dimension={breakdownDimension}
            currencySymbol={currencySymbol}
            onTabChange={onBreakdownTabChange}
            onRowClick={onBreakdownRowClick(breakdownDimension, setContext)}
            tabsConfig={tabsConfig}
            showMinorData={showMinorData}
          />
        </LazyVisible>
      </PageContent>
    </AnalyticsPage>
  )
}
